import React, { forwardRef, useState, Children, isValidElement, useRef, useMemo, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { createMemoStyles, visuallyHidden, defaultFonts, safeRest, usePreviousState, uniqueId } from '@wds/utils';
import { List } from '@wds/list';
import { IconChevronUp, IconChevronDown } from '@wds/icons';
import { useTheme } from 'react-jss';

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var SEMANTIC_COLORS = /* #__PURE__ */Object.freeze(['primary', 'success', 'info', 'warning', 'error', 'upgrade', 'secondary', 'muted']);
var SIZES = /* #__PURE__ */Object.freeze(['lg', 'md', 'sm']);
/* #__PURE__ */Object.freeze([].concat(_toConsumableArray(SIZES), ['xs']));
var DATA_VIZ_THEMES = /* #__PURE__ */Object.freeze(['data-one', 'data-two', 'data-three', 'data-four', 'data-five', 'data-six', 'data-seven', 'data-eight', 'data-nine', 'data-ten']);
var KEYS = /* #__PURE__ */Object.freeze({
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
}); // Deprecated. Moving to e.key

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var _excluded$2 = ["palette", "spacing"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var useStyles = createMemoStyles(function (_ref) {
  var palette = _ref.palette,
      spacing = _ref.spacing,
      theme = _objectWithoutProperties(_ref, _excluded$2);

  var pane = {
    bgColor: palette.background.accent,
    padding: spacing[5]
  };
  return {
    wrenchPane: function wrenchPane(_ref2) {
      var hide = _ref2.hide,
          transparent = _ref2.transparent;
      return _objectSpread(_objectSpread(_objectSpread({}, hide && visuallyHidden), defaultFonts(theme)), {}, {
        background: transparent ? 'none' : pane.bgColor
      });
    },
    wrenchPaneBody: function wrenchPaneBody() {
      return {
        padding: pane.padding
      };
    }
  };
});
var useStyles$1 = useStyles;

var _excluded$1 = ["children", "hide", "id", "padding", "labelledBy", "role", "transparent"];

var Pane = function Pane(_ref) {
  var children = _ref.children,
      hide = _ref.hide,
      id = _ref.id,
      padding = _ref.padding,
      labelledBy = _ref.labelledBy,
      role = _ref.role,
      transparent = _ref.transparent,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var theme = useTheme();

  var _useStyles = useStyles$1({
    hide: hide,
    transparent: transparent,
    theme: theme
  }),
      wrenchPane = _useStyles.wrenchPane,
      wrenchPaneBody = _useStyles.wrenchPaneBody;

  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: wrenchPane,
    role: role,
    id: id,
    "aria-labelledby": labelledBy,
    "aria-hidden": hide
  }, safeRest(rest)), padding ? /*#__PURE__*/React.createElement("div", {
    className: wrenchPaneBody
  }, children) : children);
};

Pane.displayName = "Pane";
Pane.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  hide: PropTypes.bool,
  id: PropTypes.string,
  padding: PropTypes.bool,
  labelledBy: PropTypes.string,
  role: PropTypes.string,
  transparent: PropTypes.bool
};
Pane.defaultProps = {
  hide: false,
  id: undefined,
  padding: false,
  labelledBy: undefined,
  role: undefined,
  transparent: false
};
var Pane$1 = Pane;

var AccordionItem = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var addOnLeft = _ref.addOnLeft,
      addOnRight = _ref.addOnRight,
      affordance = _ref.affordance,
      ariaControls = _ref.ariaControls,
      onKeyDown = _ref.onKeyDown,
      children = _ref.children,
      color = _ref.color,
      disabled = _ref.disabled,
      expanded = _ref.expanded,
      id = _ref.id,
      interactive = _ref.interactive,
      marked = _ref.marked,
      onClick = _ref.onClick,
      paneComponent = _ref.paneComponent,
      upgrade = _ref.upgrade;
  var tabIndex = disabled ? '-1' : 0;
  return /*#__PURE__*/React.createElement(List.Item, {
    accordion: true,
    addOnLeft: addOnLeft,
    addOnRight: addOnRight,
    affordance: affordance,
    "aria-controls": ariaControls,
    onKeyDown: onKeyDown,
    color: color,
    disabled: disabled,
    expanded: expanded,
    id: id,
    interactive: interactive,
    marked: marked,
    onClick: onClick,
    paneComponent: paneComponent,
    role: "button",
    upgrade: upgrade,
    tabIndex: tabIndex,
    ref: ref
  }, children);
});
AccordionItem.propTypes = {
  /**
   * Left addons are used to support the label by supplying it more context
   * and/or faster scanability for users. Left addons must be consistent across an entire list.
   * So if you add an avatar to one, you must add an avatar to the rest.
   */
  addOnLeft: PropTypes.node,

  /**
   * Right addons are used for secondary actions that can be taken or elements that provide a hint of extra content.
   * Unlike left addons, right addons do not need to persist throughout an entire list.
   * So if you use a button for a right addon you do not have to have one for each list item in the list.
   */
  addOnRight: PropTypes.node,

  /**
   * Display down arrow icon on right side of Accordion panel
   */
  affordance: PropTypes.node,
  ariaControls: PropTypes.string,

  /**
   * Pass in a custom onKeyDown function.
   */
  onKeyDown: PropTypes.func,

  /**
   * Content(s) of Pane inside of Accordion Item
   */
  children: PropTypes.node,

  /**
   * Set color of Item, if "marked" prop is used.
   */
  color: PropTypes.oneOf([].concat(_toConsumableArray(SEMANTIC_COLORS), _toConsumableArray(DATA_VIZ_THEMES))),

  /**
   * Disable flag for Item
   */
  disabled: PropTypes.bool,

  /**
   * Set Accordion content panel to be open or closed.
   */
  expanded: PropTypes.bool,

  /**
   * Accordion Item unique identifier
   */
  id: PropTypes.string,

  /**
   * Add hover style
   */
  interactive: PropTypes.bool,

  /**
   * Add colored marker on left side of item
   */
  marked: PropTypes.bool,

  /**
   * Pass in a custom onClick function.
   */
  onClick: PropTypes.func,

  /**
   * Add special "upgrade" highlight styles to item.
   */
  upgrade: PropTypes.bool,

  /**
   * Hidden prop for utilizing List.Item component
   */
  paneComponent: PropTypes.node
};
AccordionItem.defaultProps = {
  addOnLeft: undefined,
  addOnRight: undefined,
  affordance: undefined,
  ariaControls: undefined,
  onKeyDown: undefined,
  children: undefined,
  color: undefined,
  disabled: false,
  expanded: false,
  id: undefined,
  interactive: false,
  marked: false,
  onClick: undefined,
  paneComponent: undefined,
  upgrade: false
};
AccordionItem.displayName = 'AccordionItem';

var _excluded = ["multiple", "interactive", "noRules", "dense", "transparent", "onChange", "id", "children"];

var Accordion = function Accordion(_ref) {
  var multiple = _ref.multiple,
      interactive = _ref.interactive,
      noRules = _ref.noRules,
      dense = _ref.dense,
      transparent = _ref.transparent,
      onChange = _ref.onChange,
      id = _ref.id,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(Children.map(children, function (child, index) {
    return /*#__PURE__*/isValidElement(child) && child.props.expanded ? index : null;
  }).filter(function (val) {
    return val !== null;
  })),
      _useState2 = _slicedToArray(_useState, 2),
      activeItems = _useState2[0],
      setActiveItems = _useState2[1];

  var prevActiveItems = usePreviousState(activeItems);
  var activeItemsHasChanged = prevActiveItems !== activeItems;
  var accListRef = useRef(null);
  var uniqueAccName = useMemo(function () {
    return uniqueId('wrench-acc-');
  }, []);
  var uniqueAccPaneName = useMemo(function () {
    return uniqueId('wrench-accPane-');
  }, []);
  /**
   * @param {number} ind
   * @return {void}
   */

  var handleOnChange = function handleOnChange(evt, ind) {
    // Since dom node is a div, we can't check the disabled property
    if (evt.currentTarget !== null && evt.currentTarget.tabIndex > -1) {
      var currentActiveItems = function currentActiveItems(items) {
        if (!multiple) {
          return items[0] === ind ? [] : [ind];
        }

        if (items.indexOf(ind) >= 0) {
          return items.filter(function (item) {
            return item !== ind;
          });
        }

        return items.concat(ind);
      };

      setActiveItems(currentActiveItems);

      if (onChange) {
        var openPanels = currentActiveItems(children);
        var currentTarget = openPanels[openPanels.length - 1];
        onChange(activeItems, currentTarget);
      }
    }
  };
  /**
  * Accordion according to Accordion Design Pattern in WAI-ARIA Authoring Practices 1.1
  * see https://www.w3.org/TR/wai-aria-practices/examples/accordion/accordion.html
  *
  * Keyboard Support:
  *
  ```
  Space or Enter
  When focus is on the accordion header of a collapsed section, expands the section.
  Tab
  Moves focus to the next focusable element.
  All focusable elements in the accordion are included in the page Tab sequence.
  Shift + Tab
  Moves focus to the previous focusable element.
  All focusable elements in the accordion are included in the page Tab sequence.
  Down Arrow
  When focus is on an accordion header, moves focus to the next accordion header.
  When focus is on last accordion header, moves focus to first accordion header.
  Up Arrow
  When focus is on an accordion header, moves focus to the previous accordion header.
  When focus is on first accordion header, moves focus to last accordion header.
  Home
  When focus is on an accordion header, moves focus to the first accordion header.
  End
  When focus is on an accordion header, moves focus to the last accordion header.
  ```
  */


  var handleKeyDown = function handleKeyDown(evt, ind) {
    if (evt.key === KEYS.SPACE || evt.key === KEYS.ENTER) {
      evt.preventDefault();
      handleOnChange(evt, ind);
      return;
    }

    var accList = _toConsumableArray(accListRef.current.children).map(function (acc) {
      return acc.firstChild;
    });

    var accListLen = accList.length - 1;
    var newFocusTarget = null;

    switch (evt.key) {
      case KEYS.DOWN:
        evt.preventDefault();
        newFocusTarget = ind < accListLen ? accList[ind + 1] : accList[0];
        break;

      case KEYS.UP:
        evt.preventDefault();
        newFocusTarget = ind === 0 ? accList[accListLen] : accList[ind - 1];
        break;

      case KEYS.HOME:
        evt.preventDefault();

        var _accList = _slicedToArray(accList, 1);

        newFocusTarget = _accList[0];
        break;

      case KEYS.END:
        evt.preventDefault();
        newFocusTarget = accList[accListLen];
        break;
    }

    if (newFocusTarget !== null) {
      newFocusTarget.focus();
    }
  };
  /**
   * @param {node} paneChildren
   * @param {string} labelledBy
   * @param {string} paneId
   * @return {node}
   */


  var renderPane = function renderPane(paneChildren, labelledBy, paneId, expanded) {
    var padding = !(paneChildren.type && paneChildren.type.displayName === 'List');
    var paneProps = {
      padding: padding,
      labelledBy: labelledBy,
      hide: !expanded,
      role: 'region',
      id: paneId
    };
    return /*#__PURE__*/React.createElement(Pane$1, Object.assign({
      transparent: true
    }, paneProps), paneChildren);
  };
  /**
   *
   * @param {node} child
   * @param {number} ind
   * @param {boolean} expanded
   * @param {boolean} multiple
   * @return {node}
   */


  var renderAccordionElement = function renderAccordionElement(child, ind, expanded, multipleElement) {
    var accordionName = child.props.id || "".concat(uniqueAccName, "-").concat(ind);
    var accordionPaneName = child.props.ariaControls || "".concat(uniqueAccPaneName, "-").concat(ind);
    var paneComponent = renderPane(child.props.children, accordionName, accordionPaneName, expanded);
    return /*#__PURE__*/cloneElement(child, {
      children: child.props.title,
      expanded: expanded,
      key: accordionName,
      id: accordionName,
      collapsed: !expanded,
      ariaControls: accordionPaneName,
      paneComponent: paneComponent,
      affordance: expanded ? /*#__PURE__*/React.createElement(IconChevronUp, {
        title: "Chevron Up"
      }) : /*#__PURE__*/React.createElement(IconChevronDown, {
        title: "Chevron Down"
      }),
      onClick: function onClick(evt) {
        return handleOnChange(evt, ind);
      },
      onKeyDown: function onKeyDown(evt) {
        return handleKeyDown(evt, ind);
      },
      multiple: multipleElement
    });
  };

  var AccordionList = Children.map(children, function (child, index) {
    var isExpandedItem = activeItems.indexOf(index) >= 0; // check if a child prop changed and update state accordiongly

    var expandedProp = child ? child.props.expanded : null;
    var childExpandedChanged = isExpandedItem !== expandedProp;
    var onlyTheChildPropChanged = activeItemsHasChanged === false && childExpandedChanged;

    if (onlyTheChildPropChanged) {
      setActiveItems(Children.map(children, function (c, i) {
        return /*#__PURE__*/isValidElement(c) && c.props.expanded ? i : null;
      }).filter(function (val) {
        return val !== null;
      }));
    }

    if (! /*#__PURE__*/isValidElement(child)) {
      return null;
    }

    return child.type.displayName === 'ListItem' ? child : renderAccordionElement(child, index, isExpandedItem, multiple);
  });
  return /*#__PURE__*/React.createElement(List, Object.assign({
    ref: accListRef,
    noRules: noRules,
    interactive: interactive,
    dense: dense,
    transparent: transparent,
    id: id
  }, safeRest(rest)), AccordionList);
};

Accordion.displayName = "Accordion";
Accordion.propTypes = {
  /**
   * Accordion.Item
   */
  children: PropTypes.node.isRequired,

  /**
   * Allows more than one child Accordion. Item to be expanded at a time
   */
  multiple: PropTypes.bool,
  onChange: PropTypes.func,

  /**
   * Add hover style to all children Accordion.Item,
   */
  interactive: PropTypes.bool,
  id: PropTypes.string,

  /**
   * Reduce child Accordion.Item height and font-weight
   */
  dense: PropTypes.bool,

  /**
   * Removed background color from child Accordion.Item,
   */
  transparent: PropTypes.bool,

  /**
   * Remove divider lines
   */
  noRules: PropTypes.bool
};
Accordion.defaultProps = {
  multiple: false,
  onChange: null,
  interactive: false,
  id: null,
  dense: false,
  transparent: false,
  noRules: false
};
Accordion.Item = AccordionItem;

export { Accordion };
